import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Bell,
  ChevronLeft,
  ChevronRight,
  Download,
  MessageCircle,
  Mic,
  PlugZap,
  Sparkles,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import {
  api,
  AutoReplySettings,
  IntegrationItem,
  MeData,
  TelegramConnectionInfo,
  VoicePreference,
} from '../api/client'
import { ErrorCard } from '../components/ui/ErrorCard'
import { Toggle } from '../components/ui/Toggle'
import { RowSkeleton } from '../components/ui/Skeleton'
import { countEnabledNotifications } from './Notifications'

/**
 * Единое место для всех настроек.
 *
 * До этого экрана настройки лежали по трём адресам: голос — в профиле,
 * уведомления — на своей странице, поведение в чатах — внутри карточки на
 * экране интеграций. Последние не находил никто: человек не идёт в
 * «Интеграции» настраивать, что бот делает с его голосовыми.
 *
 * Здесь всё собрано списком. Короткое (голос) правится на месте, длинное
 * (уведомления, чаты) — отдельными экранами, а строка показывает состояние,
 * чтобы не приходилось заходить ради проверки.
 */

/** Строка-ссылка на вложенный экран настроек. */
function NavRow({
  to,
  icon,
  title,
  status,
}: {
  to: string
  icon: React.ReactNode
  title: string
  status: string
}) {
  return (
    // state.from — чтобы на экране, открытом и отсюда, и из профиля, кнопка
    // «назад» вела туда, откуда пришли, а не всегда в профиль.
    <Link
      to={to}
      state={{ from: '/settings' }}
      className="btn-active w-full p-4 flex items-center gap-3 text-left"
    >
      <span className="text-ink-2 shrink-0">{icon}</span>
      <span className="flex-1 min-w-0">
        <span className="block text-base text-ink">{title}</span>
        <span className="block text-xs text-ink-2 mt-0.5">{status}</span>
      </span>
      <ChevronRight size={16} className="text-ink-2 shrink-0" />
    </Link>
  )
}

export function Settings() {
  const qc = useQueryClient()

  const { data, isLoading, error, refetch } = useQuery<MeData>({
    queryKey: ['me'],
    queryFn: () => api.get('/api/me'),
    staleTime: 60_000,
  })

  const { data: integrations } = useQuery<IntegrationItem[]>({
    queryKey: ['integrations'],
    queryFn: () => api.get('/api/integrations'),
    staleTime: 60_000,
  })

  const { data: tgConn } = useQuery<TelegramConnectionInfo | null>({
    queryKey: ['telegram-connection'],
    queryFn: () =>
      api.get<TelegramConnectionInfo | null>('/api/telegram/connection').catch(() => null),
    staleTime: 60_000,
  })

  const { data: chatSettings } = useQuery<AutoReplySettings | null>({
    queryKey: ['auto-reply'],
    queryFn: () => api.get<AutoReplySettings>('/api/telegram/auto-reply').catch(() => null),
    enabled: !!tgConn?.is_enabled,
    staleTime: 60_000,
  })

  const voiceMutation = useMutation({
    mutationFn: (pref: VoicePreference) =>
      api.patch('/api/me/voice', { voice_preference: pref }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] }),
  })

  const isPro = data?.subscription.tier === 'pro'

  // Сколько всего включено в чатах — чтобы строка отвечала на вопрос
  // «что там сейчас» без захода внутрь.
  const chatsStatus = (() => {
    if (!tgConn?.is_enabled) return 'Не подключено'
    if (!chatSettings) return 'Подключено'
    const on = [
      chatSettings.enabled && 'автоответчик',
      (chatSettings.voice_transcript || chatSettings.incoming_voice_transcript) &&
        'расшифровка',
      chatSettings.deleted_capture && 'удалённые',
    ].filter(Boolean)
    return on.length ? `Включено: ${on.join(', ')}` : 'Подключено, ничего не включено'
  })()

  return (
    <div className="flex flex-col min-h-full page-enter">
      <div className="sticky top-0 sticky-header z-10 px-4 pt-5 pb-3">
        <Link
          to="/profile"
          className="btn-active inline-flex items-center gap-1 text-sm text-ink-1 mb-2"
        >
          <ChevronLeft size={16} /> Профиль
        </Link>
        <p className="text-h1 text-ink">Настройки</p>
      </div>

      <div className="flex-1 px-4 pb-6 space-y-5">
        {isLoading && (
          <div className="space-y-2">
            {Array.from({ length: 4 }).map((_, i) => <RowSkeleton key={i} />)}
          </div>
        )}

        {error && <ErrorCard onRetry={refetch} />}

        {data && (
          <>
            {/* ── Как я отвечаю ── */}
            <section>
              <p className="section-label">Как я отвечаю</p>
              <div className="bg-surface-1 border border-line-1 rounded-xl">
                <div className="p-4">
                  <div className="flex items-center gap-3">
                    <Mic size={18} className="text-ink-2 shrink-0" />
                    <div className="flex-1 pr-3">
                      <p className="text-base text-ink">Голосовые ответы</p>
                      <p className="text-xs text-ink-2 mt-0.5">
                        {data.voice_preference !== 'none'
                          ? 'Отвечаю голосом на голосовые сообщения'
                          : 'Отвечаю текстом'}
                      </p>
                    </div>
                    <Toggle
                      checked={data.voice_preference !== 'none'}
                      disabled={voiceMutation.isPending}
                      label="Голосовые ответы"
                      onChange={() =>
                        voiceMutation.mutate(
                          data.voice_preference !== 'none' ? 'none' : 'onyx',
                        )
                      }
                    />
                  </div>
                  {isPro && data.voice_preference !== 'none' && (
                    <div className="segmented-control mt-3">
                      {(['onyx', 'nova'] as VoicePreference[]).map(v => (
                        <button
                          key={v}
                          onClick={() => voiceMutation.mutate(v)}
                          disabled={voiceMutation.isPending}
                          className={`segment btn-active ${
                            data.voice_preference === v ? 'active' : ''
                          }`}
                        >
                          {v === 'onyx' ? 'Onyx (муж.)' : 'Nova (жен.)'}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </section>

            {/* ── Что я делаю сам ── */}
            <section>
              <p className="section-label">Что я делаю сам</p>
              <div className="bg-surface-1 border border-line-1 rounded-xl divide-y divide-line-1">
                <NavRow
                  to="/notifications"
                  icon={<Bell size={18} />}
                  title="Уведомления"
                  status={`${countEnabledNotifications(data)} включено`}
                />
                <NavRow
                  to="/settings/telegram"
                  icon={<MessageCircle size={18} />}
                  title="Чаты Telegram"
                  status={chatsStatus}
                />
              </div>
            </section>

            {/* ── Подключения ── */}
            <section>
              <p className="section-label">Подключения</p>
              <div className="bg-surface-1 border border-line-1 rounded-xl divide-y divide-line-1">
                <NavRow
                  to="/integrations"
                  icon={<PlugZap size={18} />}
                  title="Почта и календари"
                  status={
                    integrations && integrations.length > 0
                      ? `${integrations.length} подключено`
                      : 'Ничего не подключено'
                  }
                />
                <NavRow
                  to="/subscription"
                  icon={<Sparkles size={18} />}
                  title="Тариф"
                  status={
                    data.subscription.tier === 'free'
                      ? 'Free'
                      : data.subscription.tier === 'pro'
                        ? 'Pro'
                        : 'Plus'
                  }
                />
              </div>
            </section>

            {/* ── Данные ── */}
            <section>
              <p className="section-label">Данные</p>
              <div className="bg-surface-1 border border-line-1 rounded-xl">
                <NavRow
                  to="/export"
                  icon={<Download size={18} />}
                  title="Мои данные"
                  status="Выгрузка и удаление"
                />
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  )
}
